import React, { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

const STORAGE_KEY = "gamma_pwa_install_dismissed_at";
const COOLDOWN_DAYS = 14;

/**
 * Shows a small bottom-right install banner when the browser fires
 * `beforeinstallprompt`. Once dismissed, hides for COOLDOWN_DAYS.
 *
 * Notes:
 *  - On iOS Safari there is no `beforeinstallprompt`; we show an
 *    instructional banner instead when the page is loaded inside Safari
 *    and not already in standalone mode.
 */
const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

const isIos = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(ua) && !/crios|fxios/i.test(ua);
};

const recentlyDismissed = () => {
  if (typeof window === "undefined") return true;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return false;
  const ts = parseInt(raw, 10);
  if (!ts) return false;
  return Date.now() - ts < COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
};

const InstallPWAPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS: show after a short delay since `beforeinstallprompt` won't fire.
    let iosTimer = null;
    if (isIos()) {
      iosTimer = setTimeout(() => {
        if (!isStandalone() && !recentlyDismissed()) {
          setShowIosHint(true);
          setVisible(true);
        }
      }, 4500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setVisible(false);
  };

  const handleInstall = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const result = await deferred.userChoice;
      if (result?.outcome === "dismissed") dismiss();
      else setVisible(false);
    } catch {
      dismiss();
    } finally {
      setDeferred(null);
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm bottom-20 sm:bottom-6 z-[60] safe-bottom"
    >
      <div className="bg-[#0c0c0e] border border-white/10 rounded-xl shadow-[0_24px_48px_-16px_rgba(0,0,0,0.6)] backdrop-blur p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md border border-[#A98CFF]/40 bg-[#A98CFF]/12 flex items-center justify-center shrink-0">
            <Smartphone className="w-4 h-4 text-[#A98CFF]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1">
              Install Gamma Trade
            </div>
            <div className="text-sm text-white leading-snug">
              {showIosHint ? (
                <>
                  Tap{" "}
                  <span className="num text-white/85">Share</span> →{" "}
                  <span className="num text-white/85">Add to Home Screen</span>{" "}
                  for fullscreen, app-like access.
                </>
              ) : (
                <>Get the full app experience — one tap from your home screen.</>
              )}
            </div>
            {!showIosHint && deferred && (
              <button
                onClick={handleInstall}
                data-testid="pwa-install-btn"
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#A98CFF] hover:bg-[#bda3ff] text-black font-medium text-[12px] transition"
              >
                <Download className="w-3.5 h-3.5" /> Install app
              </button>
            )}
          </div>
          <button
            onClick={dismiss}
            data-testid="pwa-install-dismiss"
            aria-label="Dismiss"
            className="text-white/40 hover:text-white/80 transition shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPWAPrompt;
